import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Table, Switch, Button, Space, Popconfirm } from "antd";
import { EditOutlined, DeleteOutlined, PlusOutlined } from "@ant-design/icons";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";

import css from "./PacksComponent.module.css";
import { PacksCreatePackModal } from "../settingSectionModals/PacksCreatePackModal/PacksCreatePackModal";
import { packsActions } from "../../../store/slices/settings-slices";
import { packsService } from "../../../services/settings-services";
import { PackUpdatePackModal } from "../settingSectionModals/PackUpdatePackModal/PackUpdatePackModal";

const PacksComponent = () => {
  const dispatch = useDispatch();
  const { t, i18n } = useTranslation();
  const { packs, trigger, status } = useSelector((store) => store.packs);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isUpdateModalOpen, setIsUpdateModalOpen] = useState(false);
  const [selectedPack, setSelectedPack] = useState(null);
  const [loadingItems, setLoadingItems] = useState({});
  const [pageSize, setPageSize] = useState(10);

  const currentLanguage = i18n.language;
  const loading = status === "Loading";

  useEffect(() => {
    dispatch(packsActions.getAllPacks());
  }, [trigger, dispatch]);

  const handleCreate = () => {
    setIsCreateModalOpen(true);
  };

  const handleUpdate = (record) => {
    setSelectedPack(record);
    setIsUpdateModalOpen(true);
  };

  const handleDelete = async (id) => {
    try {
      await packsService.deleteById(id);
      dispatch(packsActions.changeTrigger());
      toast.success(t("Pack deleted successfully"));
    } catch (error) {
      console.error("Error deleting pack:", error);
      toast.error(t("Error deleting pack"));
    }
  };

  const handleActiveChange = async (record, checked) => {
    try {
      setLoadingItems((prev) => ({ ...prev, [record.id]: true }));
      await packsService.changeActive(record.id, { isActive: checked });
      dispatch(packsActions.changeTrigger());
      toast.success(t("Active status changed"));
    } catch (error) {
      console.error("Error changing active status:", error);
      toast.error(t("Error changing active status"));
    } finally {
      setLoadingItems((prev) => ({ ...prev, [record.id]: false }));
    }
  };

  const columns = [
    {
      title: t("Name"),
      dataIndex: "name",
      width: 200,
      key: "name",
      render: (name) => name[currentLanguage] || name["en"],
    },
    {
      title: t("Description"),
      dataIndex: "description",
      width: 200,
      key: "description",
      render: (description) =>
        description[currentLanguage] || description["en"],
    },
    {
      title: t("Active"),
      dataIndex: "isActive",
      key: "isActive",
      width: 120,
      render: (isActive, record) => (
        <Switch
          checked={isActive}
          onChange={(checked) => handleActiveChange(record, checked)}
          loading={loadingItems[record.id]}
          checkedChildren={t("Yes")}
          unCheckedChildren={t("No")}
        />
      ),
    },
    {
      title: t("Actions"),
      key: "actions",
      width: 150,
      render: (_, record) => (
        <Space size="middle">
          <Button
            type="primary"
            icon={<EditOutlined />}
            onClick={() => handleUpdate(record)}
            size="small"
          >
            {t("Edit")}
          </Button>
          <Popconfirm
            title={t("Are you sure you want to delete this pack?")}
            onConfirm={() => handleDelete(record.id)}
            okText={t("Yes")}
            cancelText={t("No")}
          >
            <Button
              type="primary"
              danger
              icon={<DeleteOutlined />}
              size="small"
            >
              {t("Delete")}
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <section style={{ padding: "1.5rem" }}>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: "1.5rem",
        }}
      >
        <h2>{t("Packs")}</h2>
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={handleCreate}
          size="large"
        >
          {t("Create New Pack")}
        </Button>
      </div>

      <div className={css.tableContainer}>
        <Table
          columns={columns}
          dataSource={packs}
          rowKey="id"
          loading={loading}
          pagination={{
            pageSize: pageSize,
            showSizeChanger: true,
            showTotal: (total) => `${t("Total")} ${total} ${t("items")}`,
            onChange: (page, pageSize) => setPageSize(pageSize),
          }}
          sticky={true}
          scroll={{ y: "calc(100vh - 300px)" }}
          bordered
        />
      </div>

      {isCreateModalOpen && (
        <PacksCreatePackModal setIsOpen={setIsCreateModalOpen} />
      )}

      {isUpdateModalOpen && selectedPack && (
        <PackUpdatePackModal
          setIsOpenUpdate={setIsUpdateModalOpen}
          pack={selectedPack}
        />
      )}
    </section>
  );
};

export { PacksComponent };
